/**
 * Exposes the pending-row count for the Quick Review side-nav badge.
 *
 * The count itself lives in the quickReviewCount Redux slice so the same
 * loading/error/data state can be shared by multiple places. This hook
 * keeps the urology-org guard and kicks off the fetch on mount; other
 * consumers may either reuse this hook or select
 * `state.quickReviewCount` directly.
 * @module useQuickReviewOrdersCount
 */
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isExceptionFlowForUrologyOrg } from "../../../enums/organizationTypes";
import { fetchQuickReviewCount } from "../../../redux/slice/medicalPa/quickReviewCountSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";

/**
 * Fetches the total number of pending Quick Review board rows for the
 * caller's org on mount. Only runs for urology exception-flow orgs
 * (Sunstate) — the board (and its count badge) does not exist for other
 * orgs.
 *
 * @returns loading, error, and the board pending-row count
 */
export const useQuickReviewOrdersCount = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, data } = useSelector(
    (state: RootState) => state.quickReviewCount,
  );

  const isUrologyOrg = isExceptionFlowForUrologyOrg(
    getOrgIdForFetchExternalWorklist(),
  );

  useEffect(() => {
    if (!isUrologyOrg) return;
    dispatch(fetchQuickReviewCount());
  }, [isUrologyOrg, dispatch]);

  return { loading, error, data };
};
